import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { Container, SectionLabel } from "../components/ui-kit";
import PageHero from "../components/PageHero";
import ProgramIcon from "../components/ProgramIcon";
import CampaignCard from "../components/CampaignCard";

export default function Programs() {
  const { slug } = useParams();
  const { t } = useI18n();
  const [programs, setPrograms] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [active, setActive] = useState(slug || "all");

  useEffect(() => { setActive(slug || "all"); }, [slug]);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const url = active && active !== "all" ? `/campaigns?category=${active}` : "/campaigns";
    api.get(url).then((r) => setCampaigns(r.data)).catch(() => {});
  }, [active]);

  const current = programs.find((p) => p.slug === active);

  return (
    <div>
      <PageHero
        label="Our Programs"
        title={current ? current.title : "Programs & Initiatives"}
        subtitle={current ? current.description : "Explore our focus areas and the initiatives we coordinate with trusted partners across regions."}
        crumbs={[{ label: "Programs", to: "/programs" }, ...(current ? [{ label: current.title }] : [])]}
      />

      <section className="bg-white">
        <Container className="py-14">
          <div className="flex flex-wrap gap-2.5">
            <button onClick={() => setActive("all")} data-testid="filter-all" className={`rounded-full px-4 py-2 text-sm font-semibold transition ${active === "all" ? "bg-emerald-900 text-white" : "border border-emerald-900/15 text-emerald-900 hover:bg-emerald-50"}`}>All</button>
            {programs.map((p) => (
              <Link key={p.slug} to={`/programs/${p.slug}`} data-testid={`filter-${p.slug}`} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${active === p.slug ? "bg-emerald-900 text-white" : "border border-emerald-900/15 text-emerald-900 hover:bg-emerald-50"}`}>
                <ProgramIcon name={p.icon} className="h-4 w-4" /> {p.title}
              </Link>
            ))}
          </div>

          <div className="mt-12">
            {campaigns.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-emerald-900/20 bg-sand p-16 text-center">
                <p className="font-display text-2xl font-bold text-emerald-900">Initiatives coming soon</p>
                <p className="mt-2 text-sm text-slate-500">New initiatives in this focus area are being prepared with our partners.</p>
              </div>
            ) : (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {campaigns.map((c) => <CampaignCard key={c.slug} c={c} />)}
              </div>
            )}
          </div>
        </Container>
      </section>

      {!current && (
        <section className="bg-sand">
          <Container className="py-16">
            <SectionLabel>{t("focus_title")}</SectionLabel>
            <h2 className="mt-4 font-display text-3xl font-bold text-emerald-900">All focus areas</h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {programs.map((p) => (
                <Link key={p.slug} to={`/programs/${p.slug}`} className="group flex flex-col rounded-2xl border border-emerald-900/10 bg-white p-6 transition hover:border-gold/40 hover:hh-shadow">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-800 group-hover:bg-gold group-hover:text-white"><ProgramIcon name={p.icon} /></div>
                  <h3 className="mt-4 font-display text-lg font-bold text-emerald-900">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600">{p.description}</p>
                </Link>
              ))}
            </div>
          </Container>
        </section>
      )}
    </div>
  );
}
